import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import WeatherWidget from "@/components/feature/WeatherWidget";

interface WorkReport {
  id: string;
  user_id: string;
  site_name: string;
  work_type: string;
  work_description: string;
  worker_count: number;
  work_hours: number;
  region: string;
  weather: string;
  work_date: string;
  created_at: string;
  profile?: { name: string; role: string } | null;
}

interface DashboardSummary {
  todayReports: number;
  totalWorkers: number;
  totalHours: number;
  activeSites: number;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary>({
    todayReports: 0,
    totalWorkers: 0,
    totalHours: 0,
    activeSites: 0,
  });
  const [recentReports, setRecentReports] = useState<WorkReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchDashboard() {
      setLoading(true);
      setError("");
      try {
        const allReports = await api.get<WorkReport[]>("/reports");

        const today = new Date().toISOString().split("T")[0];
        const todayData = allReports.filter(r => r.work_date === today);
        const totalWorkers = todayData.reduce((sum, r) => sum + (r.worker_count || 0), 0);
        const totalHours = todayData.reduce((sum, r) => sum + (r.work_hours || 0), 0);
        const uniqueSites = new Set(todayData.map(r => r.site_name)).size;

        setSummary({
          todayReports: todayData.length,
          totalWorkers,
          totalHours,
          activeSites: uniqueSites,
        });

        const recent = [...allReports]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 5);

        setRecentReports(recent);
      } catch {
        setError("데이터를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    }

    fetchDashboard();
  }, []);

  const statCards = [
    { label: "오늘 작성된 일보", value: summary.todayReports, icon: "ri-file-text-line", unit: "건", color: "bg-amber-50 text-amber-600" },
    { label: "전체 투입 인원", value: summary.totalWorkers, icon: "ri-team-line", unit: "명", color: "bg-emerald-50 text-emerald-600" },
    { label: "총 작업 시간", value: summary.totalHours, icon: "ri-time-line", unit: "시간", color: "bg-rose-50 text-rose-600" },
    { label: "진행 중 현장", value: summary.activeSites, icon: "ri-building-line", unit: "개소", color: "bg-indigo-50 text-indigo-600" }
  ];

  const workTypeColors: Record<string, string> = {
    "철근": "bg-orange-100 text-orange-700",
    "골조": "bg-yellow-100 text-yellow-700",
    "토공": "bg-amber-100 text-amber-700",
    "콘크리트": "bg-stone-100 text-stone-700",
    "마감": "bg-teal-100 text-teal-700",
    "설비": "bg-cyan-100 text-cyan-700",
    "전기": "bg-sky-100 text-sky-700",
    "안전": "bg-red-100 text-red-700"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">대시보드 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-red-400 text-2xl"></i>
        </div>
        <p className="text-sm text-gray-600 mb-3">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">대시보드</h1>
          <p className="text-sm text-gray-400 mt-0.5">오늘의 작업 현황을 한눈에 확인하세요</p>
        </div>
        <button
          onClick={() => navigate("/report/new")}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          일보 작성
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">{card.label}</span>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${card.color}`}>
                <i className={`${card.icon} text-sm`}></i>
              </div>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-gray-900">{card.value}</span>
              <span className="text-sm text-gray-400">{card.unit}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-gray-900">최근 작업일보</h2>
            <button
              onClick={() => navigate("/reports")}
              className="text-sm text-amber-600 hover:text-amber-700 font-medium cursor-pointer whitespace-nowrap"
            >
              전체보기
            </button>
          </div>
          {recentReports.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-file-text-line text-gray-300 text-lg"></i>
              </div>
              <p className="text-sm text-gray-400">아직 작성된 작업일보가 없습니다</p>
              <button
                onClick={() => navigate("/report/new")}
                className="mt-3 text-sm text-amber-600 hover:text-amber-700 font-medium cursor-pointer"
              >
                첫 일보 작성하기
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentReports.map((report) => (
                <div
                  key={report.id}
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/report/${report.id}`)}
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-text-line text-amber-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{report.site_name}</p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{report.work_description}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${workTypeColors[report.work_type] || "bg-gray-100 text-gray-600"}`}>
                      {report.work_type}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">{report.work_date}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="bg-white rounded-xl p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">금주 작업 현황</h2>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">주간 일보 누계</span>
              <span className="text-base font-bold text-gray-900">{summary.todayReports}건</span>
            </div>
          </div>
          <WeatherWidget />
        </div>
      </div>
    </div>
  );
}